import { TimeoutAbort, createTimeoutReadableStream } from "./timeout/index.js";
export * from "./node.js";
export * from "./timeout/index.js";
/**
 * Class representing a response object with optional timeout management for its body stream.
 */
export class TimeoutResponse {
    response;
    timeoutAbort;
    createBody;
    _body;
    /**
     * You should not new a `TimeoutResponse` on your own.
     */
    constructor(response, timeoutAbort, createBody) {
        this.response = response;
        this.timeoutAbort = timeoutAbort;
        this.createBody = createBody;
    }
    /**
     * Getter for response headers.
     */
    get headers() {
        return this.response.headers;
    }
    /**
     * Getter for the `ok` status of the response.
     */
    get ok() {
        return this.response.ok;
    }
    /**
     * Getter for whether the response was redirected.
     */
    get redirected() {
        return this.response.redirected;
    }
    /**
     * Getter for the response status code.
     */
    get status() {
        return this.response.status;
    }
    /**
     * Getter for the status text of the response.
     */
    get statusText() {
        return this.response.statusText;
    }
    /**
     * Getter for the response type.
     */
    get type() {
        return this.response.type;
    }
    /**
     * Getter for the response URL.
     */
    get url() {
        return this.response.url;
    }
    /**
     * If you want to cancel the body, use this function instead of `body.cancel()` or `body.getReader().cancel()`.
     */
    async cancelBody() {
        if (typeof this._body !== "undefined") {
            if (this._body !== null) {
                await this._body.cancel();
            }
        }
        else if (this.response.body) {
            await this.response.body.cancel();
        }
        if (typeof this.timeoutAbort !== "undefined") {
            this.timeoutAbort.abort();
        }
    }
    /**
     * Getter for the body stream of the response. If `createBody` is provided, it will be used to create a body stream with timeout management.
     */
    get body() {
        if (typeof this._body === "undefined") {
            if (typeof this.createBody !== "undefined") {
                this._body = this.createBody();
            }
            else {
                this._body = this.response.body;
            }
        }
        return this._body;
    }
    /**
     * Getter for whether the body has already been used.
     */
    get bodyUsed() {
        return this.response.bodyUsed;
    }
    /**
     * Reads and returns the body content as a string.
     *
     * @returns {Promise<string>} The body content in text form.
     */
    async text() {
        if (this.body === null) {
            return "";
        }
        const reader = this.body.getReader();
        const decoder = new TextDecoder();
        let text = "";
        for (;;) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            text += decoder.decode(value, { stream: true });
        }
        return text;
    }
    /**
     * Reads and parses the body content as JSON.
     *
     * @template T The expected JSON type.
     * @returns {Promise<T>} The parsed JSON object.
     */
    async json() {
        const text = await this.text();
        return JSON.parse(text);
    }
}
/**
 * A utility function to perform a fetch request with optional request and idle timeouts.
 *
 * @param {string | URL} input The URL or request input for the fetch.
 * @param {TimeoutRequestInit} [init] Options for the fetch request, including request timeout and idle timeout.
 *
 * @returns {Promise<TimeoutResponse>} A `TimeoutResponse` instance wrapping the fetch response.
 *
 * @throws {AbortError} If the request is aborted due to a timeout.
 */
export const timeoutFetch = async (input, init) => {
    if (typeof init === "undefined"
        || (typeof init.idleTimeout === "undefined"
            && typeof init.requestTimeout === "undefined")) {
        const response = await fetch(input, init);
        return new TimeoutResponse(response);
    }
    const timeoutAbort = new TimeoutAbort(init.requestTimeout);
    const options = init;
    options.signal = timeoutAbort.signal;
    if (typeof init.idleTimeout === "number") {
        if (options.body instanceof ReadableStream) {
            options.body = createTimeoutReadableStream(options.body, timeoutAbort, init.idleTimeout);
        }
        else {
            timeoutAbort.resetTimeout(init.idleTimeout);
        }
    }
    let response;
    try {
        response = await fetch(input, options);
    }
    catch (error) {
        timeoutAbort.abort();
        throw error;
    }
    if (typeof init.idleTimeout === "number") {
        if (!(options.body instanceof ReadableStream)) {
            timeoutAbort.clearTimeout();
        }
    }
    let createBody;
    if (response.body !== null) {
        const body = response.body;
        createBody = () => createTimeoutReadableStream(body, timeoutAbort, init.idleTimeout, true);
    }
    else {
        timeoutAbort.abort();
    }
    return new TimeoutResponse(response, timeoutAbort, createBody);
};
/**
 * Utility function to check if an error is of type `AbortError`.
 *
 * @param {Error} error The error to check.
 *
 * @returns {boolean} `true` if the error is an `AbortError`, otherwise `false`.
 */
export const isAbortError = (error) => error.name === "AbortError";
